import React, { useState } from 'react';
import classNames from 'classnames';

import Icons from 'assets/icons';

import { Box } from 'ui/box';

interface ComparisonAccordionProps {
  title: string;
  children: React.ReactNode;
  /**
   * Each accordion owns its own expand state, so opening one never collapses another - the design
   * calls for independent panels rather than a single-open-at-a-time accordion.
   */
  defaultExpanded?: boolean;
  testId?: string;
}

const ComparisonAccordion = ({
  title,
  children,
  defaultExpanded = false,
  testId,
}: ComparisonAccordionProps): React.JSX.Element => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <Box
      sx={{
        borderWidth: '1px',
        borderColor: 'tokens.navy.100',
        borderRadius: '0.375rem',
        overflow: 'hidden',
      }}
      data-testid={testId}
    >
      <Box
        component="button"
        type="button"
        sx={[
          {
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            width: '100%',
            px: 1.5,
            py: 1.25,
            '&:hover': { bgcolor: 'tokens.gray.50' },
            transitionProperty:
              'color, background-color, border-color, text-decoration-color, fill, stroke',
            transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
            transitionDuration: '150ms',
          },
          isExpanded && {
            borderBottomWidth: '1px',
            borderColor: 'tokens.navy.100',
          },
        ]}
        onClick={() => setIsExpanded((prev) => !prev)}
        aria-expanded={isExpanded}
      >
        <Icons.Arrow
          className={classNames('size-3 shrink-0 text-gray-500', {
            'rotate-90': isExpanded,
          })}
          aria-hidden="true"
        />
        <span className="text-base font-bold text-gray-700 leading-6 text-left">
          {title}
        </span>
      </Box>

      {isExpanded && <Box sx={{ p: 1.5 }}>{children}</Box>}
    </Box>
  );
};

export default ComparisonAccordion;
